import { isEmpty } from 'lodash-es'
import moment from 'moment-timezone'

import { isCalendlyMeeting } from './meeting_helpers'

const LOCATION_TYPES = [
  'gotomeeting',
  'gotomeeting_conference',
  'zoom',
  'zoom_conference',
  'google_conference',
  'microsoft_teams_conference',
  'webex_conference'
]

export const validatedExternalLocation = (event) => LOCATION_TYPES.includes(event.locationType) && event.externalLocation

export const hasEventLocationIssue = (event) => {
  const externalLocation = validatedExternalLocation(event)
  return externalLocation && externalLocation.showLocationIssue
}

export const hasEventPaymentIssue = ({ hasUncapturedCharges }) => hasUncapturedCharges

export const isCanceledEvent = ({ cancelled, status }) => cancelled || status === 'CANCELED'

export const isExpiredEvent = ({ expired }) => expired

export const eventHasInternalConflict = (event, { otherEventsOnDay, currentUserUuid }, scopeType) => {
  if (isEmpty(otherEventsOnDay)) return false
  if (scopeType === 'all_users_and_teams') return false
  if (!isCalendlyMeeting(event)) return false
  // Only show Conflict badge when current user is the host (e.g. hide when Owner/Admin views another user's meetings).
  // If event has no owner field, fall through to normal overlap logic so we don't incorrectly hide the badge.
  if (currentUserUuid != null) {
    const eventOwnerUuid = event.ownerUserUuid ?? event.owner_user_uuid
    if (eventOwnerUuid != null && eventOwnerUuid !== currentUserUuid) return false
  }
  const { startTime, endTime, uuid } = event
  const eventStart = moment(startTime)
  const eventEnd = moment(endTime)

  return otherEventsOnDay.some((otherEvent) => {
    if (otherEvent.uuid === uuid) return false
    const otherEventStart = moment(otherEvent.startTime)
    const otherEventEnd = moment(otherEvent.endTime)
    // See if the current event overlaps another event in anyway, including being completely during
    if (eventStart.isBetween(otherEventStart, otherEventEnd, 'minute')
      || eventEnd.isBetween(otherEventStart, otherEventEnd, 'minute')) return true

    // Final check to see if other event is completely during the current event
    return eventStart.isSameOrBefore(otherEventStart, 'minute') && eventEnd.isSameOrAfter(otherEventEnd, 'minute')
  })
}

export const allInviteesDeclined = (event) => {
  if (event.cancellation?.canceledByInvitee) return true
  if (!event.attendees?.length) return false
  const attendeesWithRsvp = event.attendees.filter((attendee) => attendee.rsvp !== undefined)
  if (!attendeesWithRsvp.length) return false
  return attendeesWithRsvp.every((attendee) => attendee.rsvp === 'declined')
}

export const didInviteeNoShow = ({ invitee, status }) => invitee?.noShow || status === 'NO_SHOW'

export const wasEventRescheduled = ({ oldSchedulable, status }) => !isEmpty(oldSchedulable) || status === 'RESCHEDULED'

export const hasEventPassed = ({ past }) => past

export const isEventInProgress = (event) => {
  if (!event.startTime || !event.endTime) return false

  const start = moment(event.startTime)
  const end = moment(event.endTime)
  const now = moment()

  return now.isBetween(start, end)
}

export const isEventToday = (event) => {
  if (!event.startTime) return false

  const start = moment(event.startTime)
  return moment().isSame(start, 'day')
}

export const isEventInTheFuture = (event) => {
  if (!event.startTime) return false

  const start = moment(event.startTime)
  return moment().isBefore(start)
}

export const isEventNext = (event, { otherEventsOnDay }) => {
  if (!event.startTime) return false
  if (isEmpty(otherEventsOnDay)) return true

  otherEventsOnDay = otherEventsOnDay
    .filter((e) => (
      !isCanceledEvent(e)
      && e.startTime
      && (isEventInProgress(e) || isEventInTheFuture(e))
    ))
    .map(({ startTime }) => moment(startTime))

  const thisEventStart = moment(event.startTime)

  return !otherEventsOnDay.some((otherDayEvent) => thisEventStart.isAfter(otherDayEvent))
}

const availableBadgeKeys = [
  {
    checkCondition: hasEventPaymentIssue,
    key: 'PAYMENT_ERROR'
  },
  {
    checkCondition: (event) => hasEventLocationIssue(event) && !hasEventPassed(event),
    key: 'ACTION_NEEDED'
  },
  {
    checkCondition: isExpiredEvent,
    key: 'EXPIRED'
  },
  {
    checkCondition: isCanceledEvent,
    key: 'CANCELLED'
  },
  {
    checkCondition: (event, options) => (
      !isCanceledEvent(event)
      && isEventToday(event)
      && isEventInTheFuture(event)
      && isEventNext(event, options)
    ),
    key: 'UP_NEXT'
  },
  {
    checkCondition: (event) => isEventInProgress(event),
    key: 'IN_PROGRESS'
  },
  {
    checkCondition: allInviteesDeclined,
    key: 'INVITEES_DECLINED'
  },
  {
    checkCondition: didInviteeNoShow,
    key: 'NO_SHOW'
  },
  {
    checkCondition: eventHasInternalConflict,
    key: 'CONFLICT'
  },
  {
    checkCondition: wasEventRescheduled,
    key: 'RESCHEDULED'
  },
  {
    checkCondition: hasEventPassed,
    key: 'PAST'
  },
]

export const getBadgeKey = (event, scopeType, { otherEventsOnDay, currentUserUuid } = {}) => {
  const options = { otherEventsOnDay, currentUserUuid }
  const badgeKey = availableBadgeKeys.find(({ checkCondition }) => checkCondition(event, options, scopeType))
  return badgeKey?.key
}
